import React, { useState, useEffect, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "./CommandPalette.scss";

const BOOT_LINES = [
  "PLANET EXPRESS TERMINAL v3.0.0",
  "GOOD NEWS, EVERYONE! CONNECTION ESTABLISHED.",
  "type 'help' to view available commands",
];

const SOCIAL_LINKS = {
  github: "https://github.com/writetosudeepto",
  linkedin: "https://www.linkedin.com/in/writetosudeepto",
  hackerrank: "https://www.hackerrank.com/writetosudeepto",
  tableau: "https://public.tableau.com/app/profile/sudipta.das5968",
};

const SECTIONS = ["home", "about", "work", "skills", "contact"];

const BENDER_QUIPS = [
  "I'm 40% Python. Ask about the other 60%.",
  "Bite my shiny metal codebase.",
  "I'm back, baby! And so are Sudipta's unit tests.",
  "Kill all humans... 's bugs. Kill all humans' bugs.",
  "This portfolio was built with 100% recycled electrons.",
];

const HELP_TEXT = [
  "AVAILABLE COMMANDS:",
  "  help              show this list",
  "  home | about | work | skills | contact",
  "                    navigate to a section",
  "  resume            download the resume PDF",
  "  theme             toggle light/dark mode",
  "  github | linkedin | hackerrank | tableau",
  "                    open profile in a new tab",
  "  whoami            identify yourself",
  "  bender            wisdom from the chief robot",
  "  clear             clear the screen",
  "  exit              close the terminal",
];

const CommandPalette = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const inputRef = useRef(null);
  const bodyRef = useRef(null);
  const closeTimerRef = useRef(null);

  const print = (lines, type = "output") => {
    const items = (Array.isArray(lines) ? lines : [lines]).map((text) => ({
      type,
      text,
    }));
    setHistory((prev) => [...prev, ...items]);
  };

  const open = useCallback(() => {
    setHistory(BOOT_LINES.map((text) => ({ type: "boot", text })));
    setInput("");
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    setIsOpen(false);
  }, []);

  // Global shortcuts + navbar button event
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setIsOpen((prev) => {
          if (!prev) {
            setHistory(BOOT_LINES.map((text) => ({ type: "boot", text })));
            setInput("");
          }
          return !prev;
        });
      } else if (e.key === "Escape") {
        close();
      }
    };
    const onToggleEvent = () => open();

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("portfolio:toggle-terminal", onToggleEvent);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("portfolio:toggle-terminal", onToggleEvent);
      clearTimeout(closeTimerRef.current);
    };
  }, [open, close]);

  // Focus input when opened, keep output scrolled to the latest line
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [history]);

  const closeSoon = (delay = 600) => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setIsOpen(false), delay);
  };

  const runCommand = (raw) => {
    const cmd = raw.trim().toLowerCase();
    if (!cmd) return;
    print(`guest@portfolio:~$ ${raw}`, "input");

    if (SECTIONS.includes(cmd)) {
      print(`Setting course for ${cmd.toUpperCase()}...`);
      document.getElementById(cmd)?.scrollIntoView({ behavior: "smooth" });
      closeSoon();
      return;
    }

    if (SOCIAL_LINKS[cmd]) {
      print(`Opening ${cmd} in a new tab...`);
      window.open(SOCIAL_LINKS[cmd], "_blank", "noreferrer");
      return;
    }

    switch (cmd) {
      case "help":
        print(HELP_TEXT);
        break;
      case "resume": {
        print("Downloading mission dossier...");
        const a = document.createElement("a");
        a.href = `${process.env.PUBLIC_URL}/resume.pdf`;
        a.download = "Sudipta_Das_Resume.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        break;
      }
      case "theme":
        print("Recalibrating visual cortex...");
        document.querySelector(".theme-toggle-navbar")?.click();
        break;
      case "whoami":
        print("guest — recruiter, collaborator, or lost space tourist.");
        break;
      case "bender":
        print(BENDER_QUIPS[Math.floor(Math.random() * BENDER_QUIPS.length)]);
        break;
      case "sudo":
      case "sudo make coffee":
        print("418: I'm a teapot. Also, Bender already drank the coffee.");
        break;
      case "clear":
        setHistory([]);
        break;
      case "exit":
      case "quit":
        print("Connection terminated. Goodbye, meatbag.");
        closeSoon(500);
        break;
      default:
        print(`command not found: ${cmd} — type 'help'`);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    runCommand(input);
    setInput("");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="command-palette-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={close}
        >
          <motion.div
            className="command-palette"
            initial={{ scale: 0.95, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 12, opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.focus();
            }}
          >
            <div className="command-palette-titlebar">
              <span className="dot red" />
              <span className="dot yellow" />
              <span className="dot green" />
              <span className="title">guest@portfolio — SUDIPTA-OS</span>
              <button className="close-btn" onClick={close} aria-label="Close terminal">
                ✕
              </button>
            </div>

            <div className="command-palette-body" ref={bodyRef}>
              {history.map((line, i) => (
                <div key={i} className={`terminal-line ${line.type}`}>
                  {line.text}
                </div>
              ))}
              <form onSubmit={onSubmit} className="terminal-input-row">
                <span className="prompt">guest@portfolio:~$</span>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                  autoCapitalize="off"
                  aria-label="Terminal command input"
                />
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
